import fb from '../../firebase'
import Vue from 'vue'
import { FirestoreDocSnapper, FirestoreSnapperType, RootState } from '../types'
import store, { storehelper, moduleActionContext } from '..'
import { VideoPlayerState } from '@/store/types'
import { firestore } from 'firebase/app'
import { ActionContext } from 'vuex'
import { VideoPlayer, VideoPlayerLastClicked, VideoPlayerPlayState } from '@/types/videoplayer'
import { AssetType, VideoAsset } from '@/types/assets'
import { contentSortByOrder } from '@/types/sceneContent'
import { SystemStatus } from '@/types/ops'
import { LogLevel } from '@/types/logs'

type VideoPlayerContext = ActionContext<VideoPlayerState, RootState>

export class StatusBlur {
  lastStatuses: SystemStatus[] = []
  count: Record<SystemStatus, number>
  maxNumStatus: number

  constructor (maxCount: number) {
    this.maxNumStatus = maxCount
    this.count = {
      [SystemStatus.ERROR]: 0,
      [SystemStatus.NETWORK_SLOW]: 0,
      [SystemStatus.NONE]: 0,
      [SystemStatus.OK]: 0,
      [SystemStatus.WARNING]: 0
    }
  }

  clear () {
    this.count = {
      [SystemStatus.ERROR]: 0,
      [SystemStatus.NETWORK_SLOW]: 0,
      [SystemStatus.NONE]: 0,
      [SystemStatus.OK]: 0,
      [SystemStatus.WARNING]: 0
    }
    this.lastStatuses = []
  }

  blurredStatus (status: SystemStatus): SystemStatus {
    this.add(status)
    return this.maxCount()
  }

  private add (status: SystemStatus) {
    this.lastStatuses.push(status)
    this.count[status]++
    if (this.lastStatuses.length > this.maxNumStatus) {
      const oldStatus = this.lastStatuses.shift()
      if (oldStatus !== undefined) this.count[oldStatus]--
    }
  }

  private maxCount (): SystemStatus {
    let maxStatus: null|SystemStatus = null
    let maxCount = 0
    Object.entries(this.count).forEach(([status, count]) => {
      if (count > maxCount) {
        maxCount = count
        maxStatus = status as SystemStatus
      }
    })
    return maxStatus ?? SystemStatus.NONE
  }
}

const statusBlur = new StatusBlur(15)

const calculateVideoPlayerStatus = (state: VideoPlayerState, id: string): SystemStatus => {
  const videoPlayer = state.videoplayers[id]
  if (videoPlayer === undefined) return SystemStatus.NONE
  const playheads = Object.values(videoPlayer.actual_playhead)
  if (playheads.length !== 2) return SystemStatus.WARNING
  if (Math.abs(playheads[0] - playheads[1]) > 5) return SystemStatus.ERROR
  const states = Object.values(videoPlayer.actual_states)
  if (states.length !== 2) return SystemStatus.WARNING
  if (states[0].video !== states[1].video) return SystemStatus.ERROR
  return SystemStatus.OK
}

interface currentSelectedClipUpdate {
  player: string,
  assetId: string|null
}

const videoPlayerModule = {
  namespaced: true,
  state: {
    videoplayers: {},
    lastClicked: {},
    localStates: {},
    currentSelectedClip: {},
    videoPlayerStatus: {}
  } as VideoPlayerState,
  getters: {
    numOfClips: (_state: VideoPlayerState) => (_videoPlayerId: string): number => {
      return store.getters.videoPlayer.orderedClips.length
    },
    currentClipIndex: (state: VideoPlayerState) => (videoPlayerId: string): number => {
      const currentClipId = state.currentSelectedClip[videoPlayerId]
      if (currentClipId === undefined || currentClipId === null) return 0
      const index = store.getters.videoPlayer.orderedClips.findIndex(asset => asset.id === currentClipId)
      return index ?? 0
    },
    orderedClips (_state: VideoPlayerState): VideoAsset[] {
      const clips: VideoAsset[] = []
      const allVideoIDs : string[] = []
      Object.values(store.state.events.currentCast.scenes).forEach(scene => {
        if (!scene.contents) return
        Object.values(scene.contents).forEach(asset => {
          if (asset.type === AssetType.Video && !allVideoIDs.includes(asset.id)) {
            const sceneAsset = store.state.assets.sceneAssets[asset.id]
            if (sceneAsset !== undefined) {
              clips.push({
                ... sceneAsset as VideoAsset,
                order: asset.order,
                muted: asset.muted ?? false,
                auto_play_next: asset.auto_play_next ?? false
              })
              allVideoIDs.push(asset.id)
            }
          }
        })
      })
      return clips.sort(contentSortByOrder)
    }

  },
  actions: {
    subscribeVideoPlayer (context: VideoPlayerContext, query: { castId: string }) {
      const { commit } = videoPlayerModuleActionContext(context)

      const key = `videoplayer_${query.castId}`
      const snapper: FirestoreDocSnapper = {
        doc: fb.db.collection('videoplayer').doc(query.castId),
        mutation: commit.updateVideoPlayerDoc,
        type: FirestoreSnapperType.Document
      }
      storehelper.subscribe(key, snapper)
    },
    unsubscribeVideoPlayer (_context: VideoPlayerContext, query: { castId: string }) {
      // console.log('unsubscribeVideoPlayer')
      const key = `videoplayer_${query.castId}`
      storehelper.unsubscribe(key, () => { store.commit.videoPlayer.cleanUpVideoPlayer() })
    },
    async playAsset (context: VideoPlayerContext, query: { playername: string, assetid: string, castid?: string }) {
      const { commit } = videoPlayerModuleActionContext(context)
      const doc = {
        [query.playername]: {
          desired_state: {
            video: query.assetid,
            paused: false,
            timestamp: (new Date()).getTime()
          }
        },
         currentSelectedClip: {
          [query.playername]: query.assetid
        }
      }
      commit.setLastPlayClicked(query)
      commit.calculateLocalStates()
      const castid = query.castid ?? store.state.events.currentCast.id
      await fb.db.collection('videoplayer').doc(castid).set(doc, { merge: true })
      setTimeout(() => { commit.calculateLocalStates() }, 1900)
    },
    async pauseAsset (context: VideoPlayerContext, query: { playername: string, assetid: string, castid?: string }) {
      const { commit } = videoPlayerModuleActionContext(context)
      const doc = {
        [query.playername]: {
          desired_state: {
            video: query.assetid,
            paused: true,
            timestamp: (new Date()).getTime()
          }
        }
      }
      commit.setLastPauseClicked(query)
      commit.calculateLocalStates()
      const castid = query.castid ?? store.state.events.currentCast.id
      await fb.db.collection('videoplayer').doc(castid).set(doc, { merge: true })
      setTimeout(() => { commit.calculateLocalStates() }, 1900)
    },
    async stopAsset (context: VideoPlayerContext, query: { playername: string, assetid: string, castid?: string }) {
      const { commit } = videoPlayerModuleActionContext(context)
      const doc = {
        [query.playername]: {
          desired_state: {
            video: '',
            paused: true,
            timestamp: (new Date()).getTime()
          }
        }
      }
      commit.setLastStoppedClicked(query)
      commit.calculateLocalStates()
      const castid = query.castid ?? store.state.events.currentCast.id
      await fb.db.collection('videoplayer').doc(castid).set(doc, { merge: true })
      setTimeout(() => { commit.calculateLocalStates() }, 1900)
    },
    async setCueIn (_context: VideoPlayerContext, query: { castId: string, playername: string, assetId: string, cueIn: number }) {
      const doc = {[query.playername]: {
        desired_state: {
          clipProperties: {
            [query.assetId]: {
              cueIn: query.cueIn
            }
          }
        }
      }}
      await fb.db.collection('videoplayer').doc(query.castId).set(doc, {merge: true})
    },
    async setCurrentSelectedClip (context: VideoPlayerContext, params: currentSelectedClipUpdate ) {
      const { state } = videoPlayerModuleActionContext(context)
      let assetId = params.assetId
      if (params.assetId === null ||
          state.localStates[params.player] === undefined ||
          state.localStates[params.player][params.assetId] === undefined) {
        assetId = null
      }
      const updates = { currentSelectedClip: { [params.player]: assetId } }
      await fb.db.collection('videoplayer').doc(store.state.events.currentCast.id).set(updates, { merge: true })
    },
    async updateSelectedClipIfNecessary (context: VideoPlayerContext) {
      const { state } = videoPlayerModuleActionContext(context)
      let updates = {}
      let needsUpdates = false
      for (const key in state.videoplayers) {
        const clips = Object.keys(state.localStates[key])
        if (!state.currentSelectedClip[key] && clips.length > 0) {
          updates = { currentSelectedClip: {
            [key]: clips[0]
          }}
          needsUpdates = true
        }
        if (state.currentSelectedClip[key] && clips.length === 0) {
          updates = { currentSelectedClip: {
            [key]: null
          }}
          needsUpdates = true
        }
      }
      if (needsUpdates) {
        await fb.db.collection('videoplayer').doc(store.state.events.currentCast.id).set(updates, { merge: true })
      }
    },
  },
  mutations: {
    updateVideoPlayerDoc (videoPlayer: VideoPlayerState, snap: firestore.DocumentSnapshot) {
      const snapdata = snap.data()
      if (snapdata) {
        if (snapdata.currentSelectedClip !== undefined) {
          Vue.set(videoPlayer, 'currentSelectedClip', snapdata.currentSelectedClip)
          delete snapdata.currentSelectedClip
        }
        let playername : string
        for (playername in snapdata) {
          if (playername != 'team_id') {
            if (! (playername in videoPlayer.videoplayers) ) {
              const player: VideoPlayer = new VideoPlayer(playername)
              player.fromJSON(snapdata[playername])
              Vue.set(videoPlayer.videoplayers, playername, player)
            } else {
              // console.log('STORE vue fromjson player')
              videoPlayer.videoplayers[playername].fromJSON(snapdata[playername])
            }
            const status = statusBlur.blurredStatus(calculateVideoPlayerStatus(videoPlayer, playername))
            if (videoPlayer.videoPlayerStatus[playername] !== status) {
              Vue.set(videoPlayer.videoPlayerStatus, playername, status)
              if (status === SystemStatus.ERROR) {
                store.dispatch.logs.addLog({
                  message: 'Error in clipplayer',
                  level: LogLevel.Error,
                  data: {
                    castId: store.state.events.currentCast.id,
                    name: playername,
                    state: { ...videoPlayer }
                  }
                })
              }
            }
          }
        }
      }

      store.commit.videoPlayer.calculateLocalStates()
      store.dispatch.videoPlayer.updateSelectedClipIfNecessary()
    },
    setLastPauseClicked (videoPlayer: VideoPlayerState, query: { assetid: string, playername: string }) {
      if (!videoPlayer.lastClicked[query.playername]) {
        videoPlayer.lastClicked[query.playername] = { [query.assetid]: new VideoPlayerLastClicked() }
      }
      if (!videoPlayer.lastClicked[query.playername][query.assetid]) {
        videoPlayer.lastClicked[query.playername][query.assetid] = new VideoPlayerLastClicked()
      }
      videoPlayer.lastClicked[query.playername][query.assetid].pauseTime = (new Date()).getTime()
    },
    setLastPlayClicked (videoPlayer: VideoPlayerState, query: { assetid: string, playername: string}) {
      if (!videoPlayer.lastClicked[query.playername]) {
        videoPlayer.lastClicked[query.playername] = { [query.assetid]: new VideoPlayerLastClicked() }
      }
      if (!videoPlayer.lastClicked[query.playername][query.assetid]) {
        videoPlayer.lastClicked[query.playername][query.assetid] = new VideoPlayerLastClicked()
      }
      videoPlayer.lastClicked[query.playername][query.assetid].playTime = (new Date()).getTime()
    },
    setLastStoppedClicked (videoPlayer: VideoPlayerState, query: { assetid: string, playername: string }) {
      if (!videoPlayer.lastClicked[query.playername]) {
        videoPlayer.lastClicked[query.playername] = { [query.assetid]: new VideoPlayerLastClicked() }
      }
      if (!videoPlayer.lastClicked[query.playername][query.assetid]) {
        videoPlayer.lastClicked[query.playername][query.assetid] = new VideoPlayerLastClicked()
      }
      videoPlayer.lastClicked[query.playername][query.assetid].stopTime = (new Date()).getTime()
    },
    calculateLocalStates (videoPlayer: VideoPlayerState) {
      for (const key in videoPlayer.videoplayers) {
        if (videoPlayer.localStates[key] === undefined) {
          Vue.set(videoPlayer.localStates, key, {})
        }
        for (const assetid of videoPlayer.videoplayers[key].loadedAssets()) {
          //Remove asset from local state if clip was removed
          let removedAsset = false //Flag will be true when missing asset encountered once
          for (const state of Object.values(videoPlayer.videoplayers[key].actual_states)) {
            //Check for mismatch of missing asset in actual state
            if (state.videos && !state.videos.hasOwnProperty(assetid)) {
              removedAsset = true
              break
            }
          }
          if (removedAsset) Vue.delete(videoPlayer.localStates[key], assetid)
          else { //Update state
            Vue.set(videoPlayer.localStates[key], assetid, {
              playstate: videoPlayer.videoplayers[key].getState(assetid),
              playhead: videoPlayer.videoplayers[key].getPlayhead(assetid)
            })
            if (videoPlayer.localStates[key][assetid].playstate === VideoPlayerPlayState.PLAYING &&
                videoPlayer.localStates[key][assetid].playhead > 2) {
              // if a clip is moved to the next one and is playing because of chaining, we need to adjust the
              // currentselectedclip to the one that is playing
              if (assetid !== videoPlayer.currentSelectedClip[key]) {
                store.dispatch.videoPlayer.setCurrentSelectedClip({
                  assetId: assetid,
                  player: key
                })
              }
            }
          }
        }
      }
    },
    cleanUpVideoPlayer (state: VideoPlayerState) {
      state.currentSelectedClip = {}
      state.videoplayers = {}
      state.videoPlayerStatus = {}
      state.localStates = {}
      state.lastClicked = {}
      store.commit.videoPlayer.clearStatusBlur()
    },
    clearStatusBlur () {
      statusBlur.clear()
    }
  }
} as const

export default videoPlayerModule
export const videoPlayerModuleActionContext = context => moduleActionContext(context, videoPlayerModule)
